import cx from 'classnames';
import _ from 'lodash';
import React, { ReactNode, useCallback, useRef } from 'react';
import { cloneChildren, getOnEnter, getPositionInViewport } from '../../_utils';
import { Avatar } from '../../avatar';
import { Icon, IconColor, IconID, IconProps, IconSize } from '../../icon';
import { RoutedLink } from '../../link';
import { Colors, Typography } from '../../typography';
import { Menu } from '../menu';
import styles from '../menu.module.scss';
import { MenuVariant } from '../types';

export interface MenuItemEventHandler {
  /**
   * Any props supplied
   */
  id: any;
  label: string;
  event: React.MouseEvent | React.KeyboardEvent;
  data?: any;
  imagePath?: string;
}

export interface ItemProps {
  /**
   * label for Menu Item
   */
  label: string;

  /**
   * Unique id to identify the menu. Use to Trigger the corresponding menu
   */
  id?: string;

  /**
   * Any valid node that can be rendered
   * Can be a Sub MenuItem shows the caret icon if children is passed
   */
  children?: ReactNode;

  /**
   * Highlight menu item and open the submenu if present
   * Replicate hover styles with this flag passed
   * @default false
   */
  active?: boolean;

  /**
   * left icon for Menu Item
   */
  iconName?: IconID;

  /**
   * custom left icon for Menu Item
   */
  customIcon?: IconProps;

  /**
   * Disable or not the `Item`. If a function is used, a boolean must be returned
   * @default false
   */
  disabled?: boolean;

  /**
   * additional configuration / relevant data for menu item
   *
   */
  data?: any;

  /**
   * Detects key presses and fires the given function at prop
   *
   */
  keyPressed?: (e: React.KeyboardEvent) => void;

  /**
   * Callback when the current `Item` is clicked.
   * The callback give you access to the current event and also the data passed
   * to the `Item`.
   * `({ data, event }) => ...`
   */
  onSelect?: (args: MenuItemEventHandler) => void;

  /**
   * subText to show under item name
   */
  subText?: string;

  /**
   * rightText to show text right-alligned
   * Having right text will disable right icon
   */

  rightText?: string;

  /**
   * hasRightIconInGroup: show empty icon instead of careticon on right side
   */
  hasRightIconInGroup?: boolean;

  /**
   * Tooltip when MenuItem is not disabled
   */
  itemTooltip?: string;

  /**
   * Tooltip when MenuItem is disabled
   */
  itemDisabledTooltip?: string;

  /*
   * Image path to show in case selected option needs to be represented as image
   */
  imagePath?: string;

  /**
   * Variant for Menu.
   * `This is for internal purpose only.` Sent from Menu component.
   * Used to style the component if variant is MenuVariant.SIDE_NAV
   * @default MenuVariant.DEFAULT
   */
  variant?: MenuVariant;

  /**
   * Source of the Avatar Image
   */
  avatarSrc?: string;

  /**
   * Showing statistics of number of followers to the right. Only used in case of avatar.
   * Shows a number inside a blue circle.
   */
  rightStats?: number;

  /**
   * Add "track-id" attribute for user click event in auto-track-service
   */
  trackId?: string;

  /**
   * Shows tooltip on the label
   */
  showLabelTooltip?: boolean;

  /**
   * Limit at which the label would truncate
   */
  labelTruncationLimit?: number;

  /**
   * show error state
   * @default false
   */
  hasError?: boolean;
  /**
   * href for RoutedLink Component
   */
  href?: string;
  /**
   * apply info color to subtext
   */
  enableRichSubtext?: boolean;
}

export const MenuItem: React.FC<ItemProps> = ({
  id,
  iconName,
  customIcon,
  label,
  children,
  data,
  keyPressed = _.noop,
  active = false,
  onSelect = _.noop,
  disabled = false,
  subText,
  rightText,
  hasRightIconInGroup = false,
  itemTooltip,
  itemDisabledTooltip,
  imagePath,
  variant = MenuVariant.DEFAULT,
  avatarSrc,
  rightStats,
  trackId,
  showLabelTooltip = false,
  labelTruncationLimit,
  hasError = false,
  href,
  enableRichSubtext = false,
  ...restProps
}: ItemProps) => {
  const sideNavVariant = [MenuVariant.SIDE_NAV, MenuVariant.MODAL_SIDE_NAV].includes(variant);
  const handleItemSelection = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      if (!disabled) {
        event.stopPropagation();
        onSelect({
          id,
          label,
          data,
          imagePath,
          event,
        });
      }
    },
    [data, disabled, id, imagePath, label, onSelect]
  );
  const menuRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const itemSubMenuClasses = cx(
    styles.item,
    styles.itemContainer,
    styles.itemWithLeftIcon,
    children || rightText ? styles.itemWithRightIcon : styles.withOutRightIcons,
    {
      [styles.itemActive]: active && !disabled,
      [`${styles.itemDisabled}`]: disabled,
    }
  );

  const labelClasses = cx(
    styles.itemText,
    !!iconName && !children ? styles.withOutLeftIcons : styles.itemWithLeftIcon
  );

  const typographyLabelClass = cx(styles.typographyLabel, {
    [styles.labelHighlight]: MenuVariant.SIDE_NAV && active,
  });

  const renderLeftIcon = (name: IconID) => {
    let iconColor = customIcon ? customIcon.color : IconColor.GRAY;
    const iconSize = customIcon ? customIcon.size : IconSize.MEDIUM;
    if (sideNavVariant && active) {
      iconColor = IconColor.BLUE;
    } else if (sideNavVariant) {
      iconColor = IconColor.BLACK;
    }

    return (
      iconName && (
        <div className={styles.itemIcon}>
          <Icon
            id={name as IconID}
            color={iconColor}
            size={sideNavVariant ? IconSize.LARGE : iconSize}
            className={customIcon ? customIcon.className : ''}
          />
        </div>
      )
    );
  };

  const renderAvatarIcon = () => {
    return (
      <>
        <Avatar
          name={label}
          src={avatarSrc}
          size={sideNavVariant ? 'xs' : 's'}
          className={styles.avatarOverride}
        />
      </>
    );
  };

  const hasSubMenu = (): boolean => !!children && (children as any).type === Menu;

  const renderRightCaretIcon = () => {
    if (hasSubMenu() && !rightText) {
      return (
        <div className={styles.cascadingIcon}>
          <Icon id={IconID.CARET_RIGHT} color={IconColor.GRAY} size={IconSize.MEDIUM} />
        </div>
      );
    }

    // Empty Icon added on right side to match the allignment with caret icon
    if (hasRightIconInGroup) {
      return (
        <div className={styles.cascadingIcon}>
          <Icon id={IconID.EMPTY} />
        </div>
      );
    }
    return null;
  };

  const renderCustomContent = () => <div className={styles.item}>{children}</div>;

  const setCascadingItemsDirection = () => {
    const cascadingMenuEl: HTMLDivElement = menuRef.current?.children[1] as HTMLDivElement;

    if (!cascadingMenuEl) return;

    const hoveringMenuItemEl: HTMLDivElement = menuRef?.current as HTMLDivElement;
    const hoveringMenuTop = getPositionInViewport(hoveringMenuItemEl).top;
    const cascadingMenuTop = hoveringMenuTop - 8;
    const subMenuRect: DOMRect = menuRef.current?.children[1]?.getBoundingClientRect() as DOMRect;
    let topPosition;

    const overflowBottom: boolean =
      cascadingMenuTop + (subMenuRect?.height || 0) > window.innerHeight;

    if (overflowBottom) {
      const distanceToBottom = window.innerHeight - hoveringMenuTop;
      topPosition = `${((subMenuRect?.height || 0) - distanceToBottom) * -1}px`;
      cascadingMenuEl.style.top = topPosition;
    }
  };

  const renderSubMenu = () =>
    hasSubMenu() && !rightText && cloneChildren(children, { isSubMenu: true, show: true });

  const getTooltipConfig = () => {
    if (showLabelTooltip) {
      return {
        dataTooltipContent: label,
        ...(labelTruncationLimit && {
          disableTooltip: label.length < labelTruncationLimit,
        }),
      };
    }
    return undefined;
  };

  const renderLabel = () => (
    <>
      <div className={labelClasses}>
        <Typography variant="p" className={typographyLabelClass} tooltipConfig={getTooltipConfig()}>
          {label}
        </Typography>

        {!!subText && (
          <Typography
            className={cx({
              [styles.subText]: enableRichSubtext,
            })}
            variant={enableRichSubtext ? 'footnote' : 'p'}
            color={enableRichSubtext ? Colors.info : Colors.grayLight}
          >
            {subText}
          </Typography>
        )}
      </div>
      {!!rightText && (
        <div className={styles.rightText}>
          <Typography variant="p" color={Colors.grayLight}>
            {rightText}
          </Typography>
        </div>
      )}
    </>
  );

  const getErrorIcon = () => {
    return (
      <Icon
        id={IconID.EXCLAMATION_POINT_CIRCLE}
        color={IconColor.RED}
        size={IconSize.SMALL}
        data-testid="menu-item-error-icon"
      />
    );
  };

  const renderChildren = () => (
    <>
      {!!children && (children as any).type !== Menu ? (
        renderCustomContent()
      ) : (
        <div
          className={cx(styles.itemInfo, {
            [styles.modalSideNavVariant]: variant === MenuVariant.MODAL_SIDE_NAV,
          })}
          role="tab"
          tabIndex={-1}
          onFocus={setCascadingItemsDirection}
          onMouseOver={setCascadingItemsDirection}
        >
          {avatarSrc ? renderAvatarIcon() : renderLeftIcon(iconName!)}
          {renderLabel()}
          {rightStats && <div className={styles.rightStats}>{rightStats}</div>}
          {hasError && getErrorIcon()}
          {renderRightCaretIcon()}
        </div>
      )}
      {renderSubMenu()}
    </>
  );
  const linkProps = {
    href,
    noUderline: true,
  };
  const props = {
    className: itemSubMenuClasses,
    onClick: handleItemSelection,
    id,
    ref: menuRef,
    'data-testid': id,
    role: 'option',
    'aria-selected': active,
    tabIndex: 0,
    onKeyDown: getOnEnter(handleItemSelection, keyPressed),
    'data-tooltip-content': disabled ? itemDisabledTooltip : itemTooltip,
    'track-id': trackId,
    ...(href && linkProps),
    ...restProps,
  };
  const component = href ? RoutedLink : 'div';
  const element = React.createElement(component as any, props, renderChildren());
  return element;
};
